import { FormGroup, IntField, useField, useForm, useIsDisabled } from '@ff-it/form';
import { Box, Select } from '@ff-it/ui';
import { FormInlineError } from 'components';
import { useFieldValue } from 'hooks';
import type { Outlet } from 'modules/supplier/outletOrServiceCategory/types';
import type { ReactElement } from 'react';

import { BandGroupTables } from '../common/BandGroupTables';
import { distributionColumns } from '../common/distributionColumns';
import { distributionModeLabel } from '../common/packages';
import { weekdaySpotKey } from '../common/weekdays';
import type {
  DistributionMode,
  RadioPackageDistribution,
  RadioPackagePricing,
  RadioTimeBands,
  SpotAxis,
} from '../types';
import { createDistribution, getDistributionTotal, getParticipatingOutletKeys, optionField } from './formValues';

const distributionModeOptions = (['PERIOD_TOTAL', 'WEEK_ALIGNED'] as const).map((value) => ({
  value,
  label: distributionModeLabel(value),
}));

export function DistributionModeField({ name = '' }: { name?: string }): ReactElement {
  const form = useForm();
  const disabled = useIsDisabled();
  const fieldName = optionField(name, 'distribution.mode');
  const {
    input: { name: selectName, value, onBlur, onFocus },
  } = useField<DistributionMode | null>(fieldName, { subscription: { value: true }, allowNull: true });
  return (
    <FormGroup label="Distribution" id={fieldName}>
      <Select
        name={selectName}
        inputId={fieldName}
        disabled={disabled}
        value={value as any}
        onBlur={onBlur}
        onFocus={onFocus}
        placeholder="No distribution"
        simple
        isClearable
        options={distributionModeOptions}
        onChange={(mode) => {
          const nextMode = mode as unknown as DistributionMode | null;
          form.change(optionField(name, 'distribution'), nextMode ? createDistribution(nextMode) : null);
        }}
      />
      <FormInlineError name={fieldName} className="mt-1" />
    </FormGroup>
  );
}

function DistributionMatrix({
  name,
  bands,
  mode,
}: {
  name: string;
  bands: RadioTimeBands;
  mode: DistributionMode;
}): ReactElement {
  const spotsName = optionField(name, 'distribution.spots');
  // `axis === null` is the single period bucket; a weekday axis adds its own segment
  const spotField = (bandKey: string, axis: SpotAxis): string =>
    axis === null ? `${spotsName}.${bandKey}` : `${spotsName}.${bandKey}.${weekdaySpotKey(axis)}`;

  return (
    <BandGroupTables
      bands={bands}
      columns={distributionColumns(mode, (bandKey, axis) => (
        <IntField name={spotField(bandKey, axis)} className="mb-0 text-right" size="sm" />
      ))}
    />
  );
}

export function DistributionTotals({
  distribution,
  pricing,
  outlets,
}: {
  distribution: RadioPackageDistribution | null;
  pricing: RadioPackagePricing;
  outlets: Outlet[];
}): ReactElement | null {
  if (!distribution) return null;

  const total = getDistributionTotal(distribution);
  const outletByKey = new Map(outlets.map((outlet) => [`_${outlet.id}`, outlet]));
  const participants = getParticipatingOutletKeys(pricing)
    .map((key) => outletByKey.get(key))
    .filter((outlet): outlet is Outlet => Boolean(outlet));

  return (
    <Box mt="1" fontSize="sm">
      {participants.length ? (
        <>
          <Box as="span" fontWeight="semibold">
            {total} spots
          </Box>{' '}
          per outlet · {participants.map((outlet) => outlet.name).join(', ')}
        </>
      ) : (
        <Box color="gray600">Add pricing for an outlet to show its spot total.</Box>
      )}
    </Box>
  );
}

function FormDistributionTotals({ name, outlets }: { name: string; outlets: Outlet[] }): ReactElement | null {
  const distribution = useFieldValue<RadioPackageDistribution | null>(optionField(name, 'distribution'), {
    allowNull: true,
  });
  const pricing = useFieldValue<RadioPackagePricing>(optionField(name, 'pricing'), { allowNull: false });
  return <DistributionTotals distribution={distribution} pricing={pricing} outlets={outlets} />;
}

export function DistributionEditor({
  name = '',
  bands,
  outlets,
}: {
  name?: string;
  bands: RadioTimeBands;
  outlets: Outlet[];
}): ReactElement {
  const mode = useFieldValue<DistributionMode | null>(optionField(name, 'distribution.mode'), {
    allowNull: true,
  });

  return (
    <Box>
      <FormInlineError name={optionField(name, 'distribution')} className="mb-1" />
      {mode && (
        <Box mt="2">
          <FormInlineError name={optionField(name, 'distribution.spots')} className="mb-1" />
          <Box style={{ overflowX: 'auto' }}>
            <DistributionMatrix name={name} bands={bands} mode={mode} />
          </Box>
          <FormDistributionTotals name={name} outlets={outlets} />
        </Box>
      )}
    </Box>
  );
}
